import { Box, Button, Stack, Typography } from "@mui/material";
import React from "react";
import {
  ConfirmInput,
  CustomPasswordInput,
  EmailInput,
  PasswordInput,
} from "../ui-elements/inputs";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  forgotSchemaOne,
  forgotSchemaTwo,
} from "../../validate/authentication";
import { useMutation } from "@tanstack/react-query";
import { useNotificationService } from "../../hooks/notification";
import { LoadingButton } from "@mui/lab";
import { btnStyOne } from "../ui-elements/styles-button";
import { OTP } from "../otp-container";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { PATH_AUTH } from "../../routes/path";
import {
  apiPasswordReset,
  apiSendForgotPasswordEmail,
} from "../../api/authentication";

type ForgotDetailsEmailProps = {
  next: () => void;
  prev: () => void;
  setDetails: React.Dispatch<
    React.SetStateAction<{
      email: string;
      password: string;
      otp: string;
      confirm_password: string;
    }>
  >;
  details: {
    email: string;
    password: string;
    otp: string;
    confirm_password: string;
  };
};
export const ForgotDetailsEmail = ({
  next,
  prev,
  details,
  setDetails,
}: ForgotDetailsEmailProps) => {
  const { showNotification } = useNotificationService();
  const {
    register,
    formState: { errors, isSubmitSuccessful },
    handleSubmit,
  } = useForm({
    resolver: zodResolver(forgotSchemaOne),
  });
  const mutation = useMutation({
    mutationFn: apiSendForgotPasswordEmail,
    onSuccess: (data) => {
      // Handle error
      if (data?.status === "error") {
        if ("details" in data) {
          showNotification(data?.details ?? "", "error");
          return;
        }
      }
      //  Handle success
      if (data?.status == 200) {
        showNotification(
          data?.data?.message ?? "Email sent successful",
          "success"
        );
        next();
      }
    },
    onError: (error) => {
      console.error("Process failed", error);
      // Handle error
      showNotification("Process failed", "error");
    },
  });
  const on_submit = async (values: any): Promise<void> => {
    const { email } = values;
    setDetails((prev) => ({ ...prev, email: email }));
    const data = { email: email };
    mutation.mutate(email);
  };
  return (
    <Stack
      direction={"column"}
      justifyContent={"space-between"}
      sx={{ margin: 2, gap: 2 }}
    >
      <Box sx={{}}>
        <Typography
          color="black"
          variant="h3"
          sx={{
            fontWeight: 500,
            letterSpacing: "-0.23999999463558197px",
          }}
        >
          Reset Password
        </Typography>
        <Typography variant="body1" sx={{ color: "#7991A4" }}>
          Enter email to reset your password
        </Typography>
        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit(on_submit)}
        >
          <EmailInput
            errors={errors}
            labelText="Email"
            placeholder="Enter email"
            register={register}
          />
          <Link to={PATH_AUTH.signIn} style={{ color: "#000" }}>
            <Typography sx={{ mb: 2 }} variant="body2">
              Login
            </Typography>{" "}
          </Link>

          <LoadingButton
            loading={mutation.isPending}
            type="submit"
            variant="contained"
            sx={btnStyOne}
          >
            Continue
          </LoadingButton>
        </Box>
      </Box>
    </Stack>
  );
};

export const ForgotDetailsOTP = ({
  next,
  prev,
  details,
  setDetails,
}: ForgotDetailsEmailProps) => {
  const [otp, setOtp] = React.useState("");
  const theme = useTheme();
  const { showNotification } = useNotificationService();

  const handleNext = () => {
    if (otp.length < 5) {
      showNotification("OTP should be 6 digits", "error");
      return;
    }
    setDetails((prev) => ({ ...prev, otp: otp }));
    next();
  };
  return (
    <Box sx={{}}>
      <Typography
        color="black"
        variant="h3"
        sx={{
          fontWeight: 500,
          letterSpacing: "-0.23999999463558197px",
        }}
      >
        Input code
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: theme.palette.secondary.contrastText }}
      >
        Kindly check your email and input the code sent to {details.email}
      </Typography>
      <Box sx={{ my: 4 }}>
        <OTP
          separator={<span>-</span>}
          value={otp}
          onChange={setOtp}
          length={6}
        />
        <Button onClick={prev}>Back</Button>
        <Stack sx={{ my: 4 }}>
          <LoadingButton
            loading={false}
            type="submit"
            onClick={handleNext}
            variant="contained"
            sx={{ ...btnStyOne, width: "80%", mx: "auto" }}
          >
            Confirm Account
          </LoadingButton>
        </Stack>
      </Box>
    </Box>
  );
};

export const ForgotDetailsPassword = ({
  next,
  prev,
  details,
  setDetails,
}: ForgotDetailsEmailProps) => {
  const theme = useTheme();
  const { showNotification } = useNotificationService();
  const navigate = useNavigate();
  const {
    register,
    formState: { errors, isSubmitSuccessful },
    handleSubmit,
  } = useForm({
    resolver: zodResolver(forgotSchemaTwo),
  });
  const mutation = useMutation({
    mutationFn: apiPasswordReset,
    onSuccess: (data) => {
      // Handle error
      if (data?.status === "error") {
        if ("details" in data) {
          showNotification(data?.details ?? "", "error");
          return;
        }
      }
      //  Handle success
      if ("data" in data) {
        showNotification(
          data?.data?.message ?? "Password reset successful",
          "success"
        );
        navigate(PATH_AUTH.signIn);
      }
    },
    onError: (error) => {
      console.error("Process failed", error);
      // Handle error
      showNotification("Process failed", "error");
    },
  });
  const on_submit = async (values: any): Promise<void> => {
    const { password, confirmPassword } = values;
    
    const data = {
      email: details.email,
      otp: details.otp,
      password: password,
      confirm_password: confirmPassword,
    };
    mutation.mutate(data);
  };
  
  return (
    <Box sx={{}}>
      <Typography
        color="black"
        variant="h3"
        sx={{
          fontWeight: 500,
          letterSpacing: "-0.23999999463558197px",
        }}
      >
        Set up new password
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: theme.palette.secondary.contrastText }}
      >
        Kindly input new password and confirm
      </Typography>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(on_submit)}
      >
        <PasswordInput
          errors={errors}
          placeholder="Enter password"
          labelText="Password"
          register={register}
        />
        <ConfirmInput
          errors={errors}
          placeholder="Confirm password"
          labelText="Confirm Password"
          register={register}
        />
        <Button onClick={prev}>Back</Button>
        <Stack sx={{ my: 4 }}>
          <LoadingButton
            loading={mutation.isPending}
            type="submit"
            variant="contained"
            sx={{ ...btnStyOne, width: "80%", mx: "auto" }}
          >
            Submit
          </LoadingButton>
        </Stack>
      </Box>
    </Box>
  );
};
